<template>
  <div class="brycloud-modern">
    <!-- Header Section -->
    <div class="brycloud-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-file-signature title-icon"></i>
          Assinatura Digital
        </h1>
        <p class="page-subtitle">
          Faça upload do seu PDF para assinatura digital
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="brycloud-container">
      <!-- Error State -->
      <div v-if="error == 'access_denied'" class="error-state">
        <div class="error-card">
          <div class="error-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="error-content">
            <h3 class="error-title">Sessão Expirada</h3>
            <p class="error-message">
              Sua sessão para assinatura expirou. Solicite uma nova assinatura
              para continuar.
            </p>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <div v-if="error != 'access_denied'" class="upload-section">
        <div class="upload-card">
          <!-- Upload Area -->
          <div
            class="upload-area"
            :class="{ 'drag-over': isDragOver, 'has-file': selectedFile }"
          >
            <div class="upload-content">
              <!-- File Input -->
              <input
                ref="fileInput"
                type="file"
                name="pdf"
                accept=".pdf,application/pdf"
                @change="handleFileSelect"
                :disabled="loading"
                class="file-input"
                id="pdf-input"
              />

              <!-- Upload Visual -->
              <div v-if="!selectedFile" class="upload-visual">
                <div class="upload-icon">
                  <i class="fas fa-cloud-upload-alt"></i>
                </div>
                <h3 class="upload-title">Selecionar arquivo PDF</h3>
                <p class="upload-description">
                  Clique aqui ou arraste seu arquivo PDF para fazer upload
                </p>
                <label for="pdf-input" class="upload-button">
                  <i class="fas fa-file-pdf mr-2"></i>
                  Escolher arquivo
                </label>
                <div class="upload-info">
                  <p class="info-text">
                    <i class="fas fa-info-circle mr-1"></i>
                    Máximo <strong>10MB</strong> - Apenas arquivos PDF
                  </p>
                </div>
              </div>

              <!-- File Preview -->
              <div v-if="selectedFile" class="file-preview">
                <div class="preview-header">
                  <div class="file-icon">
                    <i class="fas fa-file-pdf"></i>
                  </div>
                  <div class="file-details">
                    <h4 class="file-name">{{ selectedFile.name }}</h4>
                    <p class="file-size">
                      {{ formatFileSize(selectedFile.size) }}
                    </p>
                  </div>
                  <button
                    @click="removeFile"
                    class="remove-button"
                    type="button"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <div class="preview-actions">
                  <label for="pdf-input" class="change-file-btn">
                    <i class="fas fa-sync-alt mr-2"></i>
                    Alterar arquivo
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div v-if="loading" class="progress-section">
            <div class="progress-header">
              <span class="progress-label">Enviando arquivo...</span>
              <span class="progress-percentage">{{ uploadProgress }}%</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
          </div>

          <!-- Actions -->
          <div class="actions-section">
            <button
              @click="execute"
              :disabled="!selectedFile || loading"
              class="submit-button"
              type="button"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
              <i v-else class="fas fa-signature mr-2"></i>
              {{ loading ? "Processando..." : "Assinar Documento" }}
            </button>
          </div>

          <!-- Success State -->
          <div v-if="identificador" class="success-state">
            <div class="success-card">
              <div class="success-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="success-content">
                <h3 class="success-title">Documento Assinado!</h3>
                <p class="success-message">
                  Seu documento foi assinado com sucesso. O arquivo assinado foi
                  aberto em uma nova aba.
                </p>
                <div class="success-id">
                  <strong>ID da Assinatura:</strong> {{ identificador }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Errors -->
      <ErrorsMessages :errors="errors" />
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import api from "../services/api";

export default {
  name: "BRyCloud",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      identificador: null,
      code: null,
      loading: false,
      selectedFile: null,
      uploadProgress: 0,
      error: null,
      state: null,
      certificates: [],
      errors: [],
      isDragOver: false,
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    handleFileSelect(event) {
      this.errors = [];
      const file = event.target.files[0];
      if (!file) return;

      // Validar se é PDF
      if (file.type !== "application/pdf") {
        this.errors = [
          { message: "Por favor, selecione apenas arquivos PDF." },
        ];
        return;
      }

      // Validar tamanho (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.errors = [{ message: "O arquivo deve ter no máximo 10MB." }];
        this.clearFileInput();
        return;
      }

      this.selectedFile = file;
    },

    retrieve() {
      let { code, error, state } = this.$router.history.current.query;
      this.code = code;
      this.error = error;
      this.state = state;
    },

    async execute() {
      try {
        this.errors = [];
        this.error = null;
        let { code, state, selectedFile } = this;

        if (!selectedFile) {
          this.errors = [{ message: "Selecione um arquivo PDF primeiro" }];
          return;
        }

        this.loading = true;
        this.uploadProgress = 0;

        const formData = new FormData();
        formData.append("user_id", state);
        formData.append("code", code);
        formData.append("pdf", selectedFile);

        const response = await api.post("/bry/user/sign-pdf", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            this.uploadProgress = percentCompleted;
          },
        });

        let { error, errors, documentos, identificador } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.identificador = identificador;
          for (let documento of documentos) {
            if (documento.links) {
              for (let link of documento.links) {
                window.open(link.href, "_blank");
              }
            }
          }

          this.removeFile();
        }

        this.selectedFile = null;
        this.loading = false;
        this.uploadProgress = 0;
      } catch (error) {
        this.selectedFile = null;
        this.loading = false;
        this.uploadProgress = 0;
        this.errors = [
          { message: "Erro ao processar o arquivo. Tente novamente." },
        ];
        this.removeFile();
      }
    },

    removeFile() {
      this.clearFile();
      this.errors = [];
    },

    clearFile() {
      this.selectedFile = null;
      this.clearFileInput();
    },

    clearFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.brycloud-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

/* Header Section */
.brycloud-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 8px 0 0 0;
  font-size: 16px;
}

/* Container */
.brycloud-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Error State */
.error-state {
  margin-bottom: 32px;
}

.error-card {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.error-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.error-content {
  flex: 1;
}

.error-title {
  font-size: 18px;
  font-weight: 600;
  color: #991b1b;
  margin: 0 0 4px 0;
}

.error-message {
  color: #991b1b;
  margin: 0;
  font-size: 14px;
}

/* Upload Section */
.upload-section {
  margin-bottom: 32px;
}

.upload-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Upload Area */
.upload-area {
  padding: 40px 32px;
  border: 2px dashed #cbd5e1;
  margin: 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  transition: all 0.3s ease;
  position: relative;
}

.upload-area:hover {
  border-color: #0f65b0;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(15, 101, 176, 0.1);
}

.upload-area.has-file {
  border-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.upload-content {
  text-align: center;
}

/* Upload Visual */
.upload-visual {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  margin-bottom: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(15, 101, 176, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(15, 101, 176, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(15, 101, 176, 0);
  }
}

.upload-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.upload-description {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  text-decoration: none;
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3);
}

.upload-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4);
}

.upload-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.info-text {
  color: #6b7280;
  font-size: 12px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* File Preview */
.file-preview {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.file-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.file-details {
  flex: 1;
}

.file-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  word-break: break-word;
}

.file-size {
  color: #64748b;
  font-size: 12px;
  margin: 0;
}

.remove-button {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 12px;
}

.remove-button:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.preview-actions {
  text-align: center;
}

.change-file-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: transparent;
  color: #0f65b0;
  border: 2px solid #0f65b0;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-size: 12px;
}

.change-file-btn:hover {
  background: #0f65b0;
  color: white;
}

/* Progress Section */
.progress-section {
  padding: 24px 32px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.progress-percentage {
  font-size: 12px;
  font-weight: 600;
  color: #0f65b0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0f65b0 0%, #1e40af 100%);
  transition: width 0.3s ease;
  border-radius: 4px;
}

/* Actions Section */
.actions-section {
  padding: 24px 32px;
  text-align: center;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.submit-button {
  display: inline-flex;
  align-items: center;
  padding: 16px 32px;
  background: linear-gradient(135deg, #1e884d 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(30, 136, 77, 0.3);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(30, 136, 77, 0.4);
}

.submit-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Success State */
.success-state {
  margin-top: 24px;
}

.success-card {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #86efac;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.success-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.success-content {
  flex: 1;
}

.success-title {
  font-size: 18px;
  font-weight: 600;
  color: #065f46;
  margin: 0 0 4px 0;
}

.success-message {
  color: #065f46;
  margin: 0 0 8px 0;
  font-size: 14px;
}

.success-id {
  color: #059669;
  font-size: 12px;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .brycloud-container {
    padding: 0 16px;
  }

  .brycloud-header {
    padding: 20px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .upload-area {
    padding: 24px 20px;
    margin: 16px;
  }

  .upload-icon {
    width: 64px;
    height: 64px;
    font-size: 24px;
  }

  .upload-title {
    font-size: 18px;
  }

  .upload-description {
    font-size: 13px;
  }

  .preview-header {
    padding: 16px;
    flex-wrap: wrap;
  }

  .actions-section,
  .progress-section {
    padding: 20px;
  }

  .submit-button {
    padding: 14px 24px;
    font-size: 14px;
  }

  .error-card,
  .success-card {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .upload-card {
    border-radius: 16px;
  }

  .upload-area {
    margin: 12px;
    padding: 20px 16px;
  }

  .file-name {
    font-size: 14px;
  }

  .preview-header {
    flex-direction: column;
    text-align: center;
  }
}

/* Utility Classes */
.mr-1 {
  margin-right: 4px;
}
.mr-2 {
  margin-right: 8px;
}
.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>